// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setInitialValues from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetInitialValues";
import { getCurrentEditingUser, hasChanges } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AssignShipToAddressModal from "@insite/content-library/Widgets/UserSetup/AssignShipToAddressModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@keller/App/Components/Common/XngZone";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { getIsAdminAllowedToDeactivateUsers } from "@keller/App/Store/Selectors/SettingsSelectors";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomAppState) => ({
    editingUser: getCurrentEditingUser(state),
    // XNG-Changes: edit disableSaveButton flag
    // disableSaveButton:
    //     !hasChanges(state) ||
    //     !!state.pages.userSetup.emailErrorMessage ||
    //     !!state.pages.userSetup.firstNameErrorMessage ||
    //     !!state.pages.userSetup.lastNameErrorMessage,
    disableSaveButton: getCurrentEditingUser(state)?.properties?.phone === state.xUserSetup?.phone,
    hasChanges: hasChanges(state),
    // XNG-Changes
    isAdminAllowedToDeactivateUsers: getIsAdminAllowedToDeactivateUsers(state),
});

const mapDispatchToProps = { setInitialValues };

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface UserSetupHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    actionButtonsHidden?: HiddenProps;
    assignShipToAddressButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    overflowMenuClickables?: ClickablePresentationProps;
    overflowMenuSaveClickableHidden?: HiddenProps;
    overflowMenuCancelClickableHidden?: HiddenProps;
    // XNG-Changes
    siteMsgStyles?: InjectableCss;
}

export const userSetupHeaderStyles: UserSetupHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 20px;
        `,
    },
    titleGridItem: {
        width: [10, 10, 8, 7, 7],
    },
    actionsGridItem: {
        width: [2, 2, 4, 5, 5],
        css: css`
            justify-content: flex-end;
        `,
    },
    assignShipToAddressButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    cancelButton: {
        // XNG-Changes
        // color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    // XNG-Changes
    siteMsgStyles: {
        css: css`
            margin-bottom: 20px;
        `,
    },
};

const styles = userSetupHeaderStyles;

const UserSetupHeader = ({
    editingUser,
    disableSaveButton,
    hasChanges,
    setInitialValues,
    // XNG-Changes
    id,
    isAdminAllowedToDeactivateUsers,
}: Props) => {
    const [isAssignShipToAddressModalOpen, setIsAssignShipToAddressModalOpen] = useState(false);
    if (!editingUser) {
        return null;
    }

    const assignShipToAddressClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsAssignShipToAddressModalOpen(true);
    };

    const assignShipToAddressModalCloseHandler = () => setIsAssignShipToAddressModalOpen(false);

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.titleGridItem}>
                    {createWidgetElement("Basic/PageTitle", {
                        fields: { overrideTitle: `${translate("Edit User")} - ${editingUser.userName}` },
                    })}
                </GridItem>
                <GridItem {...styles.actionsGridItem}>
                    <Hidden {...styles.actionButtonsHidden} below="lg">
                        <Button
                            {...styles.assignShipToAddressButton}
                            type="button"
                            onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                                assignShipToAddressClickHandler(event)
                            }
                            data-test-selector="userSetup_assignShipToAddress"
                        >
                            {translate("Assign Ship To Address")}
                        </Button>
                    </Hidden>
                    <Hidden {...styles.actionButtonsHidden} below="md">
                        {hasChanges && (
                            <Button
                                {...styles.cancelButton}
                                type="button"
                                onClick={() => setInitialValues({ userId: editingUser.id })}
                                data-test-selector="userSetupHeader_cancelButton"
                            >
                                {translate("Cancel")}
                            </Button>
                        )}
                    </Hidden>
                    <Hidden {...styles.actionButtonsHidden} below="md">
                        <Button
                            {...styles.saveButton}
                            type="submit"
                            disabled={disableSaveButton}
                            data-test-selector="userSetupHeader_saveButton"
                        >
                            {translate("Save")}
                        </Button>
                    </Hidden>
                    <Hidden {...styles.actionButtonsHidden} above="md">
                        <OverflowMenu position="end" {...styles.overflowMenu}>
                            <Clickable
                                {...styles.overflowMenuClickables}
                                onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                                    assignShipToAddressClickHandler(event)
                                }
                            >
                                {translate("Assign Ship To Address")}
                            </Clickable>
                            <Hidden {...styles.overflowMenuCancelClickableHidden} above="sm">
                                <Clickable
                                    {...styles.overflowMenuClickables}
                                    onClick={() => setInitialValues({ userId: editingUser.id })}
                                    disabled={!hasChanges}
                                >
                                    {translate("Cancel")}
                                </Clickable>
                            </Hidden>
                            <Hidden {...styles.overflowMenuSaveClickableHidden} above="sm">
                                <Clickable
                                    {...styles.overflowMenuClickables}
                                    type="submit"
                                    disabled={disableSaveButton}
                                    onClick={() => {}}
                                >
                                    {translate("Save")}
                                </Clickable>
                            </Hidden>
                        </OverflowMenu>
                    </Hidden>
                </GridItem>
            </GridContainer>
            <AssignShipToAddressModal
                isModalOpen={isAssignShipToAddressModalOpen}
                closeModalHandler={assignShipToAddressModalCloseHandler}
            />
            <XngZone contentId={id} zoneName="Content1" hint="Zone For Rich content" />
            {!isAdminAllowedToDeactivateUsers && (
                <StyledWrapper {...styles.siteMsgStyles}>
                    <Typography>{siteMessage("User_Administration_Message")}</Typography>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupHeader),
    definition: {
        group: "User Setup",
        displayName: "Header",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
